import axios, { AxiosInstance } from "axios";

export interface StrapiLocale {
  id: number;
  name: string;
  code: string;
  createdAt: string;
  isDefault: boolean;
}

export interface StrapiEntry {
  id: number;
  attributes: Record<string, unknown>;
}

export interface StrapiPagination {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface StrapiCollectionResponse {
  data: StrapiEntry[];
  meta?: { pagination?: StrapiPagination };
}

export default class StrapiClient {
  axios: AxiosInstance;
  baseUrl: string | undefined;

  constructor(strapiBaseUrl?: string) {
    // Highest precedence: Environment variable (for local development)
    const envStrapiUrl = import.meta.env.NANO_PLAYER_FRONTEND_STRAPI_URL;
    if (envStrapiUrl) {
      // In development mode, use proxy to avoid CORS issues
      if (import.meta.env.DEV) {
        this.baseUrl = "/strapi";
      } else {
        this.baseUrl = envStrapiUrl;
      }
    } else {
      // Second precedence: localStorage override (for dev)
      const urlOverride = localStorage.getItem("strapiApiUrlOverride");
      if (urlOverride) {
        this.baseUrl = urlOverride;
      } else {
        this.baseUrl = strapiBaseUrl;
      }
    }
    this.axios = axios.create({
      headers: {
        "Content-type": "application/json",
      },
    });
  }

  isEnabled() {
    return this.baseUrl && this.baseUrl != "";
  }

  fetchContent(path: string, locale: string) {
    return new Promise((resolve, reject) => {
      this.axios
        .get(this.baseUrl + "/" + path + "?populate=deep&locale=" + locale)
        .then((response) => {
          resolve(response.data);
        })
        .catch((e) => reject(e));
    });
  }

  /**
   * Fetches a collection endpoint with a caller-built query string, for requests that need
   * pagination, sorting, field selection or filters beyond the fixed `fetchContent` query.
   */
  fetchCollection(path: string, queryString: string): Promise<StrapiCollectionResponse> {
    return this.axios.get<StrapiCollectionResponse>(this.baseUrl + "/" + path + "?" + queryString).then((response) => response.data);
  }

  fetchLocales(): Promise<StrapiLocale[]> {
    return new Promise((resolve, reject) => {
      this.axios
        .get(this.baseUrl + "/i18n/locales")
        .then((response) => {
          resolve(response.data);
        })
        .catch((e) => reject(e));
    });
  }
  fetchContentByNameId(path: string, nameId: string, locale: string) {
    return new Promise((resolve, reject) => {
      this.axios
        .get(`${this.baseUrl}/${path}?populate=deep&filters[nameId][$eq]=${nameId}&locale=${locale}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((e) => reject(e));
    });
  }
  fetchCasinoPages(locale: string) {
    return this.fetchContent("/casino-pages", locale);
  }
}
